// assets/product-custom-property.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {((event: Event) => void) | null} */
  #boundPickerChange = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.toggleByVariant === 'true') {
      const section = this.closest('.shopify-section');
      section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
      section?.addEventListener(ThemeEvents.variantSelected, this.#onVariantUpdate);

      const picker = section?.querySelector('variant-picker');
      if (picker) {
        this.#boundPickerChange = () => {
          this.#syncVisibility(this.#getCurrentVariantTitle());
        };
        picker.addEventListener('change', this.#boundPickerChange);
      }

      // İlk yükleme (URL’de İsme Özel seçili olabilir)
      requestAnimationFrame(() => {
        this.#syncVisibility(this.#getCurrentVariantTitle());
      });
    }
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    section?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantUpdate);

    if (this.#boundPickerChange) {
      section?.querySelector('variant-picker')?.removeEventListener('change', this.#boundPickerChange);
      this.#boundPickerChange = null;
    }
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  #onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    const fromResource = [resource?.title, resource?.option1, resource?.option2, resource?.option3]
      .filter(Boolean)
      .join(' ');
    const title = fromResource || this.#getCurrentVariantTitle();
    this.#syncVisibility(title);
  };

  #getCurrentVariantTitle() {
    const section = this.closest('.shopify-section');
    const checked = section?.querySelectorAll('variant-picker input[type="radio"]:checked');
    if (checked?.length) {
      return Array.from(checked)
        .map((el) => /** @type {HTMLInputElement} */ (el).value)
        .filter(Boolean)
        .join(' ');
    }

    const select = section?.querySelector('variant-picker select');
    if (select instanceof HTMLSelectElement && select.value) {
      return select.value;
    }

    const variantJson = section?.querySelector('variant-picker script[type="application/json"]');
    if (variantJson?.textContent) {
      try {
        const variant = JSON.parse(variantJson.textContent);
        return [variant?.title, variant?.option1, variant?.option2, variant?.option3].filter(Boolean).join(' ');
      } catch {
        return '';
      }
    }

    return '';
  }

  /** Türkçe karakter / büyük-küçük harf normalize */
  #normalize(str) {
    return String(str || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #matchesVariant(title) {
    const n = this.#normalize(title);
    if (!n) return false;

    // Açıkça kişiselleştirme olmayan seçimler
    if (/\bisimsiz\b/.test(n)) return false;
    if (/^(standart|standard|default|normal)$/.test(n)) return false;

    const showValue = this.#normalize(this.dataset.variantShowValue || '');
    if (showValue && (n === showValue || n.includes(showValue))) return true;

    // Yaygın Türkçe varyant adları
    if (/\bisme\s*ozel\b/.test(n)) return true;
    if (/\bisimliye\b/.test(n)) return true;
    if (/\bisimli\b/.test(n)) return true;

    return false;
  }

  #syncVisibility(variantTitle) {
    const shouldShow = this.#matchesVariant(variantTitle);
    this.style.display = shouldShow ? 'block' : 'none';
    this.hidden = !shouldShow;

    const { textInput } = this.refs;
    if (!textInput) return;

    if (!shouldShow) {
      textInput.value = '';
      textInput.required = false;
      textInput.removeAttribute('aria-required');
      textInput.setAttribute('disabled', 'disabled');
      this.#updateCharacterCount();
      return;
    }

    textInput.removeAttribute('disabled');
    if (this.dataset.requiredWhenVisible === 'true') {
      textInput.required = true;
      textInput.setAttribute('aria-required', 'true');
    }
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
